import { Injectable } from '@angular/core';
import { environment } from '@myenv/environment';

interface DemoDataConfig {
  enabled?: boolean;
  customerId?: number | string;
  firstName?: string;
}

interface StoredUser {
  customerId?: unknown;
  firstName?: unknown;
}

/**
 * Gate for the "nextgen" showcase pages.
 *
 * When the active environment defines `demoData` (dev only) and the logged-in
 * user matches the configured identity (customerId + firstName), components
 * should keep rendering their static/mock data instead of hitting the API.
 *
 * Usage in a component:
 *   if (!this.demoData.isDemoUser()) {
 *     // load real data from the API
 *   }
 *
 * Because `demoData` is only present in the dev environment files, this always
 * returns false in test/staging/prod builds.
 */
@Injectable({ providedIn: 'root' })
export class DemoDataService {
  private readonly config: DemoDataConfig =
    (environment as unknown as { demoData?: DemoDataConfig }).demoData ?? {
      enabled: false,
    };

  /** True when demo mode is on AND the current user is the configured demo user. */
  isDemoUser(): boolean {
    if (!this.config?.enabled) {
      return false;
    }
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }
    const idMatches =
      String(user.customerId ?? '').trim() ===
      String(this.config.customerId ?? '').trim();
    const nameMatches =
      String(user.firstName ?? '')
        .trim()
        .toLowerCase() ===
      String(this.config.firstName ?? '')
        .trim()
        .toLowerCase();
    return idMatches && nameMatches;
  }

  private getCurrentUser(): StoredUser | null {
    try {
      return JSON.parse(localStorage.getItem('currentUser') ?? 'null');
    } catch {
      return null;
    }
  }
}
